/**
 * tutorTrainingViewModel — ce que « Ta formation » lit dans le store.
 *
 * Patron A : le réducteur `tutorTraining` (la vidéo, les questions, le seuil, la progression, le dernier
 * résultat) et la tranche de sa lecture. Comme `TutorTrainingScreen.js` : l'attente tant qu'il n'y a ni
 * vidéo ni erreur, l'erreur seulement quand rien n'a été lu (une relecture qui échoue garde la page).
 */
import { createSelector } from "reselect"

import {
    isTrainingPassed,
    toTrainingQuestions,
    toTrainingResult,
    toTrainingVideo,
    trainingNeededCount,
    type ApiTutorTraining,
    type UITutorTrainingResult,
} from "../../../adapters/tutor/Account/tutorTrainingAdapter"
import { type UITutorTrainingQuestion, type UITutorTrainingVideo } from "../../../ui/types/tutorCompte"

export type TutorTrainingViewModel = {
    loading: boolean
    error: unknown
    /** L'étape envoyée avec les réponses. */
    stepId: number | null
    video: UITutorTrainingVideo | null
    /** La page de la vidéo, ouverte hors de l'app. */
    videoUrl: string | null
    questions: UITutorTrainingQuestion[]
    neededCount: number
    passed: boolean
    result: UITutorTrainingResult | null
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectTraining = (state: ReduxState): ApiTutorTraining | undefined => state?.data?.tutorTraining
const selectLoadError = (state: ReduxState): unknown => state?.api?.getTutorTraining?.error

export const tutorTrainingViewModel = createSelector(
    [selectTraining, selectLoadError],
    (training, loadError): TutorTrainingViewModel => {
        const step = training?.step ?? null
        const questions = toTrainingQuestions(training?.questions)
        return {
            loading: step == null && !loadError,
            error: step == null ? (loadError ?? null) : null,
            stepId: step?.id ?? null,
            video: toTrainingVideo(step),
            videoUrl: step?.video_url || step?.embed_url || null,
            questions,
            neededCount: trainingNeededCount(questions.length, training?.passThresholdPercent),
            passed: isTrainingPassed(training?.progress),
            result: toTrainingResult(training?.lastResult, training?.questions),
        }
    }
)
